import { StrictMode, useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import Layout from './view/components/Layout';
import Admin from './view/components/Admin';
import Login from './view/screens/Login/Login';
import SSOSuccessfulRedirect from './view/screens/SSO/SSOSuccessfulRedirect';
import ForgotPassword from './view/screens/ForgotPassword';
import Resourcehub from './view/screens/Resourcehub';

import { MfaModalProvider } from './shared/providers/MfaModalProvider';
import { WidgetsModalProvider } from './shared/providers/WidgetsModalProvider';
import { setup as AnalyticsSetup } from './utilities/hooks/useTracking';
import { StorageItems } from './shared/constants/app';
import {
  getItemFromLocalStorage,
  removeItemFromLocalstorage,
} from './utilities/common/storage';
import { getAuthToken, setTokenData } from './utilities/common/token';
import { exchangeRefreshToken } from './shared/service/auth';
import './config/Localisation';
import { initSentry } from './utilities/common/monitoring';
import { initStaleChunkReloader } from './utilities/common/chunkRecovery';
import FiltersProvider from './shared/providers/FiltersProvider';

import './index.css';
import MemberManagement from './view/screens/MemberManagement';

initStaleChunkReloader();

if (import.meta.env.PROD) {
  console.log = () => {};
  console.warn = () => {};
  console.debug = () => {};
  initSentry();
}

AnalyticsSetup();

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  annotationPlugin,
  ChartDataLabels,
);

const BrowserRouter = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    children: [
      {
        path: '/d/:slug',
        element: <Layout />
      },
      { path: '/resourcehub', element: <Resourcehub /> },
      { path: '/members/*', element: <MemberManagement /> },
    ],
  },
  { path: '/admin', element: <Admin /> },
  { path: '/login', element: <Login /> },
  { path: '/forgot-password', element: <ForgotPassword /> },
  { path: '/sso/success', element: <SSOSuccessfulRedirect /> },
]);

const queryClient = new QueryClient();

let restoreSessionPromise: Promise<void> | null = null;

const restoreSession = (): Promise<void> => {
  if (restoreSessionPromise) return restoreSessionPromise;

  restoreSessionPromise = (async () => {
    if (window.location.pathname === '/sso/success') return;
    if (
      window.location.pathname === '/admin' &&
      new URLSearchParams(window.location.search).get('token')
    ) {
      return;
    }
    if (getAuthToken()) return;
    if (!getItemFromLocalStorage(StorageItems.SESSION_HINT, 'string')) return;
    try {
      const tokens = await exchangeRefreshToken();
      setTokenData(tokens);
    } catch (e) {
      removeItemFromLocalstorage(StorageItems.SESSION_HINT);
    }
  })();

  return restoreSessionPromise;
};

function AuthBootstrap({ children }: { children: React.ReactNode }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let mounted = true;
    restoreSession().finally(() => {
      if (mounted) setReady(true);
    });
    return () => {
      mounted = false;
    };
  }, []);

  if (!ready) {
    return <div className="p-5 text-lg font-bold">Loading...</div>;
  }

  return <>{children}</>;
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <MfaModalProvider>
      <WidgetsModalProvider>
        <FiltersProvider>
          <QueryClientProvider client={queryClient}>
            <AuthBootstrap>
              <RouterProvider router={BrowserRouter} />
            </AuthBootstrap>
          </QueryClientProvider>
        </FiltersProvider>
      </WidgetsModalProvider>
    </MfaModalProvider>
  </StrictMode>,
);
